import 'mapbox-gl/dist/mapbox-gl.css'

import OLCollection from 'ol/Collection'
import OLLayerGroup from 'ol/layer/Group'
import OLLayer from 'ol/layer/Layer'
import { toLonLat } from 'ol/proj'
import OLSource from 'ol/source/Source'
import { reduxStore } from 'reduxStore'

import type { ConfigLayerOptions } from './ConfigLayer'
import type { StyleSpecification } from '@maplibre/maplibre-gl-style-spec'
import type MapboxGLType from 'mapbox-gl'
import type MapLibreGLType from 'maplibre-gl'
import type OLBaseEvent from 'ol/events/Event'
import type { ObjectEvent } from 'ol/Object'

import PoziAPIData from 'api/data'
import { HistoryUpdate } from 'api/history'
import * as Model from 'api/model'
import * as PoziUtils from 'api/utils'
import SetAttributions from 'config/SetAttributions'
import { setLayerLoadInProgress, setLayerLoadDone, setLayerLoadFailed } from 'drawer/tabs/layers/actions'
import PoziEvents from 'utils/events'
import { getMap } from 'utils/MapUtils'
import PoziNotifications from 'utils/notifications'

// import OLVectorTileLayer from 'ol/layer/VectorTile'

// Mapbox requires their logo to be shown. We do this in main.css
export const mapboxAttributions = [
  '© <a href="https://www.openstreetmap.org/copyright" target="_blank" rel="noopener noreferrer">OpenStreetMap contributors</a>',
  '© <a href="https://www.mapbox.com/about/maps/" target="_blank" rel="noopener noreferrer">Mapbox</a>'
]

// Maptiler would like their logo to be shown. We do this in main.css
export const maptilerAttributions = [
  '© <a href="https://www.openstreetmap.org/copyright" target="_blank" rel="noopener noreferrer">OpenStreetMap contributors</a>',
  '© <a href="https://www.maptiler.com/copyright/" target="_blank" rel="noopener noreferrer">MapTiler</a>'
]

export const createMapboxLayer = (layerConf: ConfigLayerOptions) => {
  if (!layerConf) {
    console.warn('createMapboxLayer - no layerConf provided!', layerConf)
    return
  }

  const styleUrl = layerConf.config?.spatial?.url
  if (!styleUrl) {
    console.warn('createMapboxLayer - no styleUrl provided!', layerConf)
    return
  }
  const baseUrl = new URL(styleUrl).origin

  // Mapbox render type: `mapbox', 'mapbox-gl' or 'mapbox-olms'
  const type = (layerConf.type || '').toLowerCase()

  console.debug('Mapbox createMapboxLayer', type, styleUrl)

  const accessTokenFromConfig = layerConf.config?.spatial?.accessToken || ''
  const accessTokenFromUrl = (styleUrl.match(/^.*access_token=([.-\w]+).*$/) || [])[1]
  const accessToken = accessTokenFromConfig || accessTokenFromUrl

  // For showing attributions
  const isMapbox = !!styleUrl.match(/(mapbox.com|mapbox:)/i)
  const isMaptiler = !!styleUrl.match(/(maptiler.com)/i)

  // Since OL Mapbox can return more than one layer (and of either Vector or VectorTile type), we
  // create a layer group to capture this case.
  const olLayer = new OLLayerGroup({
    opacity: layerConf.opacity || 1,
    maxResolution: layerConf.maxResolution || 100000,
    minResolution: layerConf.minResolution || 0.0001,
    visible: false // we set visibility shortly afterwards to give us a chance to retrieve the layers
  })

  // Set some useful properties to the layer
  olLayer.setProperties({
    title: layerConf.title,
    titleid: layerConf.titleid,
    group: layerConf.group,
    groupid: layerConf.groupid,
    initialised: false // custom property to determine if we have initialised this layer
  })

  // Detect and report errors

  const onError = (error: OLBaseEvent | Error | unknown) => {
    reduxStore.dispatch(setLayerLoadFailed(layerConf.titleid))

    const errorLC = String(error).toLowerCase()
    const isTimeout = errorLC.includes('timeout')
    const isWebGLError = errorLC.includes('webgl')

    let errorType = 'other'
    let message = `Something went wrong... Unable to load layer: ${layerConf.title}`
    if (isTimeout) {
      errorType = 'timeout'
      message = `Loading of layer timed out: ${layerConf.title}`
    } else if (isWebGLError) {
      errorType = 'webgl'
      message = `Unable to load layer: ${layerConf.title}. This is likely because your browser does not support WebGL.`
    } else {
      errorType = 'other'
      // @ts-expect-error
      const errorMessage = error.message || 'Unable to load layer'
      message = `Unable to load layer ${layerConf.title}: ${errorMessage}`

      console.warn('MapLibre - error other', errorMessage, error)
    }

    PoziNotifications().warning(message)

    const errorMsg = `Mapbox layer load failed: ${layerConf.title} (${type}), errorType: ${errorType}`
    PoziUtils.gtag({
      category: 'data',
      status: 'warning',
      identifier: layerConf.title,
      action: 'ajax request',
      additionalInfo: errorMsg
    })

    console.log('Mapbox error for layer ' + layerConf.titleid, error)
  }

  olLayer.on('error', onError)

  // Once the layers have been loaded and store in the LayerGroup, we
  // need to do some processing of these layers
  const processLayers = (layerCollection: OLCollection<any>) => {
    const layerGroup = olLayer.get('group') || ''
    const layerIsBasemap = layerGroup.toLowerCase() === 'basemap'

    const currentZIndex = olLayer.getZIndex() || 0

    // If the layer is a basemap, we will lower the zIndex of it
    if (layerIsBasemap) {
      olLayer.setZIndex(currentZIndex - 1) // This may not do anything useful but it's good to set it anyway
    }

    // Loop through all layers in the Collection to set some values
    layerCollection.forEach((subLayer) => {
      // If the layer is a basemap, we will lower the zIndex so the layer
      // appears below the other layers
      // Unfortunately setting the zIndex on a LayerGroup does not set
      // the zIndex on the individual layers, so we'll do this for each sub-layer
      if (layerIsBasemap) {
        subLayer.setZIndex(currentZIndex - 1)
      }

      // Set some useful properties to the sublayers
      subLayer.setProperties({
        title: layerConf.title,
        titleid: layerConf.titleid,
        group: layerConf.group,
        groupid: layerConf.groupid
      })

      // Detect and report errors
      subLayer.on('error', onError)

      // Generate layer attributions if layer is of type Mapbox
      const attributions = (isMapbox && mapboxAttributions) || (isMaptiler && maptilerAttributions)
      SetAttributions(subLayer, attributions)

      // Keep track of when source has completed loading
      // This currently does nothing as `setSourceLoaded` cannot handle VectorTiles (yet?)
      PoziUtils.setSourceLoaded(subLayer, subLayer.getSource(), layerConf)
    })
  }

  // Create OL layers as a layer collection
  const createLayers = async () => {
    let layerCollection
    try {
      switch (type) {
        case 'mapbox': {
          console.debug('Mapbox style mapbox://styles/', styleUrl)
          layerCollection = await createMapboxVectorLayerCollection()
          break
        }
        case 'mapbox-gl': {
          console.debug('Mapbox-GL', styleUrl)
          layerCollection = await createMapboxGlLayerCollection(type)
          break
        }
        case 'mapbox-olsm': // left-over from a typo. Remove when all layers are updated
        case 'mapbox-olms': {
          console.debug('Mapbox style https://', styleUrl)
          layerCollection = await createOLMSLayerCollection()
          break
        }
        case 'maplibre-gl-arcgis':
        case 'maplibre-gl': {
          console.debug('Maplibre-GL', styleUrl)
          layerCollection = await createMapboxGlLayerCollection(type)
          break
        }
        default: {
          console.debug('Mapbox unknown type:', type, styleUrl)
          throw new Error(`Unknown mapbox type: ${type} for ${styleUrl}`)
        }
      }
    } catch (error) {
      console.log('Mapbox - error creating layer', layerConf.titleid, type, error)

      onError(error)
    }

    if (!layerCollection) {
      console.log('Mapbox - no layers created. Was there an error maybe?')
      return
    }

    // Process layers before adding to group layer
    processLayers(layerCollection)

    // Finally add new layers to group layer
    olLayer.setLayers(layerCollection)
  }

  const createMapboxVectorLayerCollection = async () => {
    const OLMapboxVector = (await import('ol-mapbox-style')).MapboxVectorLayer

    const olLayer = new OLMapboxVector({
      // The rendering can be optimised for zooming using renderMode: 'vector'.
      // See: https://github.com/openlayers/openlayers/issues/13414#issuecomment-1049774020
      // renderMode: 'vector', // 'hybrid' (default) or 'vector'
      styleUrl,
      accessToken,
      declutter: true
    })

    return new OLCollection([olLayer])
  }

  const createOLMSLayerCollection = async () => {
    const { apply, applyBackground, applyStyle, default: olms } = await import('ol-mapbox-style')

    // Handle Mapbox's custom URL format
    let url = styleUrl
    if (url.startsWith('mapbox://styles/')) {
      url = url.replace('mapbox://styles/', 'https://api.mapbox.com/styles/v1/')
      if (!url.includes('access_token')) {
        url += `?access_token=${accessToken}`
      }
    }

    const options: any = {
      accessToken,
      transformRequest(url: string, type: string) {
        // console.debug('olms transform', url, type)

        // E.g:
        // https://portal.spatial.nsw.gov.au/vectortileservices/rest/services/Hosted/NSW_BaseMap_VectorTile_Hybrid/VectorTileServer/
        // needs to become:
        // https://portal.spatial.nsw.gov.au/vectortileservices/rest/services/Hosted/NSW_BaseMap_VectorTile_Hybrid/VectorTileServer
        // which will redirect to:
        // https://portal.spatial.nsw.gov.au/vectortileservices/rest/services/Hosted/NSW_BaseMap_VectorTile_Hybrid/VectorTileServer/resources/styles/root.json

        const needsTransform = url.includes('portal.spatial.nsw.gov.au/vectortileservices') || url.includes('https://enterprise.mapshare.vic.gov.au/') // VicMap

        if (!needsTransform) {
          return
        }

        console.debug('olms transform - needs transform...')

        // Transform ESRI ArcGIS Online requests
        if (type === 'Source' && url.endsWith('/')) {
          url = url.replace(/\/$/, '')
          url += '?f=json' // Needed for VicMap

          console.debug("olms transform source - remove trailing / + add '?f=json'", url)
        }

        if (type === 'Tiles' && !url.includes('/VectorTileServer/tile/')) {
          url = url.replace('/tile/', '/VectorTileServer/tile/')

          console.debug("olms transform source - add '/VectorTileServer/'", url)
        }

        return new Request(url)
      }
    }

    const olLayer = new OLLayerGroup()

    await apply(olLayer, url, options)

    console.debug('Mapbox - olms layer - apply', olLayer)

    return olLayer.getLayers()

    // ALTERNATIVE METHOD
    // const olLayer = new OLVectorTileLayer({ declutter: true })
    // applyStyle(olLayer, url, options)
    // console.debug('Mapbox - olms layer - applyStyle', olLayer)
    // return new OLCollection([olLayer])

    // OLD METHOD BELOW:
    // olms magically creates an OL map with all layers coming from the style URL
    // const tmpMap = await olms('', url, options) // we use an empty map identifier for this temp map
    // return tmpMap.getLayers() // getLayers returns a Collection, not an Array!
  }

  const createMapboxGlLayerCollection = async (type: string) => {
    let MapboxGL: typeof MapboxGLType
    let MapLibreGL: typeof MapLibreGLType
    let library: typeof MapboxGL | typeof MapLibreGL
    switch (type) {
      case 'mapbox-gl': {
        MapboxGL = (await import('mapbox-gl')).default
        library = MapboxGL
        break
      }
      case 'maplibre-gl':
      case 'maplibre-gl-arcgis': {
        MapLibreGL = (await import('maplibre-gl')).default
        // MapLibreGL = (await import('maplibre-gl/dist/maplibre-gl-dev')).default // for debugging
        library = MapLibreGL
        break
      }
      default:
        console.warn(`Unknown Mapbox/Maplibre type: '${type}'. Not proceeding.`)
        PoziNotifications().warning(`Something went wrong... Unknown Mapbox/Maplibre type: '${type}'.`)
        return
    }

    if (accessToken) {
      if (type === 'mapbox-gl') {
        // @ts-expect-error TODO: make clear that 'library' is 'mapbox-gl' here and not 'maplibre-gl'
        library.accessToken = accessToken
      } else {
        console.debug('createMapboxGlLayerCollection - Layer type Maplibre does not support access tokens. Ignoring', accessToken)
      }
    }

    // console.debug('Mapbox - MapLibre - createMapboxGlLayerCollection', type, library, typeof library)

    const urls: Record<string, string> = {}

    // Modify relative URLs to be absolute, required by Mapbox/Maplibre libraries
    const transformRequest = function (url: any, type: any) {
      console.debug('MapLibre - transformRequest', type, url)

      // If the URL doesn't have the base URL (like OSM vector tiles), add it
      if (url.startsWith('/')) {
        url = new URL(url, baseUrl).href
        return {
          url
        }
      }

      // ArcGIS online uses relative URLs. Maplibre does not support this (yet):
      // * https://github.com/maplibre/maplibre-gl-js/issues/182
      // * https://github.com/maplibre/maplibre-gl-js/pull/645
      //
      if (type === 'Source' && !url.startsWith('https://')) {
        try {
          url = new URL(url, urls.Style).href + '?f=json'
          console.debug('transformRequest new Source URL', url)
        } catch (error) {
          console.log('transformRequest - invalid URL', type, url, urls.Style)
        }
      } else {
        // console.debug('transformRequest - not transforming', type, url)
      }

      if (type === 'Tile' && !url.startsWith('https://')) {
        try {
          url = new URL(url, urls.Source).href

          // console.log('transformRequest new Tile URL', url, urls)
        } catch (error) {
          console.log('transformRequest - invalid URL', type, url)
        }
      }

      urls[type] = url

      return {
        url
      }
    }

    const olMap = getMap()

    // From: https://openlayers.org/en/latest/examples/mapbox-layer.html
    const mbMap = new library.Map({
      // style: styleUrl,
      style: '', // set the style URL later to allow us to modify the map instance
      attributionControl: false,
      boxZoom: false,

      // refreshExpiredTiles:
      // work around issue with some tiles that keep wanting to be refreshed due to
      // invalid max-age Cache-Control headers (like '1.00:00:00/)
      // See: https://trello.com/c/yd5w1zc2/847-support-esri-vector-tiles-directly
      refreshExpiredTiles: false,

      container: olMap.getTarget() || '',
      doubleClickZoom: false,
      dragPan: false,
      dragRotate: false,
      interactive: false,
      keyboard: false,
      pitchWithRotate: false,
      scrollZoom: false,
      touchZoomRotate: false
    })

    let url = styleUrl

    // If URL is to the high level entry point, change it to point to the style JSON
    if (type === 'maplibre-gl-arcgis' && !url.endsWith('/resources/styles/root.json')) {
      url = url.replace(/\/$/, '') + '/resources/styles/root.json'
    }

    // @ts-expect-error: we are overriding a non-API method. This has its risks.
    const mbUpdateStyle = mbMap?._updateStyle

    if (mbUpdateStyle) {
      // console.debug('MapLibre - updateStyle', mbUpdateStyle)

      // @ts-expect-error
      mbMap._updateStyle = function (style: any, options: any) {
        console.debug('MapLibre - _updateStyle override', style, options)

        if (!options.transformStyle && typeof style === 'string') {
          options.transformStyle = (_prev: StyleSpecification, next: StyleSpecification) => {
            console.debug('MapLibre - transformStyle override', _prev, next, style)

            if (!next) {
              return
            }

            // Handle errors within the JSON. Example:
            //  {
            //     "error": {
            //       "code": 499,
            //       "message": "Token Required.",
            //       "details": null
            //     }
            //  }

            // @ts-expect-error
            const error = next.error
            if (error) {
              console.log('MapLibre - transformStyle error', error)

              const errorMessage = error.message || 'Unknown error'
              onError(new Error(errorMessage, error))

              return
            }

            try {
              const resolveURL = (urlPath: string) => {
                const urlBase = urlPath.startsWith('/') ? baseUrl : style
                const urlString = new URL(urlPath, urlBase).href

                // use decodeURI to revert curly braces being URI encoded
                return decodeURI(urlString)
              }

              // Handle glyphs URL
              if (next.glyphs) {
                next.glyphs = resolveURL(next.glyphs)
              }

              // Handle sprite - can be string or array
              if (typeof next.sprite === 'string') {
                next.sprite = resolveURL(next.sprite)
              } else if (Array.isArray(next.sprite)) {
                next.sprite = next.sprite.map((sprite) => ({
                  ...sprite,
                  url: resolveURL(sprite.url)
                }))
              }

              if (!next.sources?.esri) {
                console.debug('MapLibre - transformStyle - no esri sources found.', next)
                return next
              }

              // Make Esri style Maplibre-friendly
              // Override Map.updateStyle to allow for handling ESRI ArcGIS VectorTile datasets
              //
              // Overrides:
              // * style URLs
              // * relative URLs are made absolute
              // * max zoom is forced
              //
              // @ts-expect-error
              const newEsriUrl = decodeURI(new URL(next.sources.esri.url, style).href).replace(/\/$/, '/?f=json')

              // @ts-expect-error
              next.sources.esri.url = newEsriUrl

              // Limit zoom levels
              const maxZoomFromConfig = layerConf.config?.maxZoom

              // @ts-expect-error
              const maxZoomFromEsri = next.sources.esri.maxzoom

              const newMaxZoom = maxZoomFromConfig ?? Math.min(maxZoomFromEsri ?? Infinity, 15)

              // @ts-expect-error
              next.sources.esri.maxzoom = newMaxZoom
              console.debug('MapLibre - transformStyle - new string style', next.sprite, next.glyphs, newEsriUrl, newMaxZoom)

              // Override VicMap white background and give it a nice grey colour
              const backGroundSourceLayerIds = [
                'Cities/Towns /FR_FRAMEWORK_AREA_POLYGON - Sea/mainland/1', // Vicmap_Vector_Tile_Basemap_Colour_WM
                'Cities/Towns /FR_FRAMEWORK_AREA_POLYGON:2/1', // Vicmap_Vector_Tile_Basemap_Hybrid_WM
                'FR_FRAMEWORK_AREA_POLYGON/1' // Vicmap_Vector_Tile_Basemap_Greyscale_WM
              ]
              const newBackgroundColour = '#f7f5f2'
              for (const id of backGroundSourceLayerIds) {
                const backGroundLayerStyle = next.layers?.find((layer) => layer.id === id)

                if (backGroundLayerStyle) {
                  // @ts-expect-error: check if we can make typescript happy
                  const fillColor = backGroundLayerStyle.paint?.['fill-color'] || ''
                  if (fillColor.toLowerCase() === '#ffffff') {
                    console.debug('MapLibre - transformStyle override - replace white background colour', id, newBackgroundColour)
                    // @ts-expect-error
                    backGroundLayerStyle.paint['fill-color'] = newBackgroundColour
                  }
                }
              }
            } catch (error) {
              console.warn('MapLibre - transformStyle - error', error)

              onError(error)

              return
            }

            return next
          }
        }

        // Call original _updateStyle function
        mbUpdateStyle.call(mbMap, style, options)
      }
    } else {
      console.warn('MapLibre - _updateStyle not found on Map instance. Cannot apply ArcGIS handling code.', mbMap)
    }

    // Transform and normalise requests for MapLibre types
    if (type === 'maplibre-gl' || type === 'maplibre-gl-arcgis') {
      // @ts-expect-error: TODO, make clear this is of type MapLibreGL
      mbMap.setTransformRequest(transformRequest)
    }

    // Make sure the the Mapbox Map always resizes when the OL map resizes
    olMap.on('change:size', (event: OLBaseEvent) => {
      // console.debug('OL map resize event. Calling mbMap.resize()...', event)
      mbMap.resize()
    })

    mbMap.on('error', function (evt) {
      const { error } = evt || {}

      // Mark the layer as failed when it fails to load the style URL (i.e. ending with root.json)
      const errorUrl = error?.url
      if (errorUrl === url) {
        console.log('MapLibre - error - style URL failed to load', errorUrl, error)

        onError(error)
      }
    })

    // For basemaps only: handle request timeouts by sending a HEAD request in parallel with the actual request.
    // If the request times out, call the onError function, which will trigger the loading of the next available
    // basemap layer
    const layerIsBasemap = layerConf.group.toLowerCase() === 'basemap'
    const layerHasMapboxUrl = url.startsWith('mapbox://')
    if (!layerHasMapboxUrl && layerIsBasemap) {
      reduxStore.dispatch(setLayerLoadInProgress(layerConf.titleid))

      PoziAPIData()
        .ajax({
          method: 'HEAD',
          timeout: 5 * 1000, // 5 second timeout,
          url,
          error: (err: any, status: string) => {
            // console.log('MapLibre - HEAD request error: ', status, err)

            if (status === 'timeout') {
              onError(new Error('MapLibre - HEAD request timeout'))
            } else {
              onError(err)
            }
          },
          success: function (result: any) {
            // console.debug('MapLibre - HEAD request success', url, result)
            reduxStore.dispatch(setLayerLoadDone(layerConf.titleid))
          }
        })
        .catch((err: any) => {
          console.log('MapLibre - HEAD request uncaught error: ', err)
        })
    }

    // Apply the style URL now the map instance is ready to process the ESRI ArcGIS VectorTile datasets
    mbMap.setStyle(url)

    // Create special layer that controls the mapbox map
    const olLayer = new OLLayer({
      render: function (frameState) {
        const canvas = mbMap.getCanvas()
        const viewState = frameState.viewState

        const visible = olLayer.getVisible()
        canvas.style.display = visible ? 'block' : 'none'
        canvas.style.position = 'absolute'

        const opacity = olLayer.getOpacity()
        canvas.style.opacity = String(opacity)

        // console.debug('Mapbox - MapLibre - opacity', opacity)

        // adjust view parameters in mapbox
        const rotation = viewState.rotation

        const newPos = {
          center: toLonLat(viewState.center),
          zoom: viewState.zoom - 1,
          bearing: (-rotation * 180) / Math.PI,
          animate: false
        }

        // @ts-ignore
        mbMap.jumpTo(newPos)

        // Cancel the scheduled update & trigger synchronous redraw
        // See: https://github.com/mapbox/mapbox-gl-js/issues/7893#issue-408992184
        // NOTE: THIS MIGHT BREAK IF UPDATING THE MAPBOX VERSION
        // @ts-ignore
        if (mbMap._frame) {
          // @ts-ignore
          mbMap._frame.cancel()
          // @ts-ignore
          mbMap._frame = null
        }
        // @ts-ignore
        mbMap._render()

        return canvas
      },

      // We need a source for setting the attributions
      source: new OLSource({})
    })

    const olLayerCollection = new OLCollection([olLayer])

    console.debug('Mapbox - MapLibre - olLayerCollection', olLayerCollection)

    return olLayerCollection
  }

  // Listen to layer changes (like opacity and visible)
  const onLayerChange = (evt: ObjectEvent) => {
    const { key, target: olLayer } = evt

    // console.debug('Mapbox - MapLibre - onLayerChange', key, olLayer, { ...evt })

    // Make all child layers have the same opacity
    if (key === 'opacity') {
      const opacity = olLayer.getOpacity()
      olLayer.getLayers().forEach((childLayer: OLLayer) => childLayer.setOpacity(opacity))
    }
  }

  // A bit of a convoluted way to detect the first time the layer is visible to
  // trigger the loading of the Mapbox layers. This makes sure that the layer only
  // gets loaded when needed (avoids unneccessary data fetching)
  const onLayerFirstTimeVisible = () => {
    const isInitialised = olLayer.get('initialised')
    if (isInitialised) {
      console.info('Mapbox / MapLibre layer already initialised. We should never get here.', olLayer.get('titleid'))
      return
    }

    const isVisible = olLayer.getVisible()
    if (isVisible) {
      console.debug('Mapbox / MapLibre - onLayerFirstTimeVisible', olLayer.get('titleid'))

      olLayer.set('initialised', true)

      // Trigger loading of Mapbox layers
      createLayers()

      olLayer.on('change:opacity', onLayerChange)
      olLayer.on('change:visible', onLayerChange)
    } else {
      console.info('Mapbox / MapLibre layer not visible yet. We should never get here.', olLayer.get('titleid'))

      // Listen once more for visibility change. Should now be visible.
      olLayer.once('change:visible', onLayerFirstTimeVisible)
    }
  }

  // Only start processing layers when the OL map has become available
  // Note: this assumes that at this point in time the OL map has not been initialised
  const mapListener = Model.on('app.map.olMap', () => {
    Model.un('app.map.olMap', mapListener) // stop listening (since we only want to do this once)
    // console.debug('Mapbox / MapLibre - mapListener', olLayer.get('titleid'), olLayer.getVisible(), PoziUtils.isLayerSelected(layerConf, true))

    // Process Mapbox layers when first first visible, either immediately or on next visibility change
    const isVisible = olLayer.getVisible()
    if (isVisible) {
      onLayerFirstTimeVisible()
    } else {
      olLayer.once('change:visible', onLayerFirstTimeVisible)
    }

    // Check first if the layer is already visible (e.g. directly from URL or as a linked layer to
    // a layer defined in the URL). If not, we determine if it should be.
    const layerIsSelected = isVisible || PoziUtils.isLayerSelected(layerConf, true)

    // Listen to changes in layer visibility and broadcast through Model()
    PoziEvents().onLayerVisible(olLayer, layerIsSelected)

    // Update history when visibility changes.
    // Note: shouldn't this go into PoziEvents().onLayerVisible()?
    olLayer.on('change:visible', () => HistoryUpdate())

    // Set layer's visibility as determined by the config
    olLayer.setVisible(layerIsSelected)

    // Store layer in the model
    Model.set('app.map.ol.layer.' + layerConf.titleid, olLayer)
  })

  return olLayer
}
